import {
    getMediaTrack,
    getActiveClipAtMs,
    mapEffectiveMsToSourceMs,
    computeTimelineDurationMs
} from '../timelineModel.js'
import { getTransitionAt } from '../compositor/transitions.js'
import { renderClipFrame, renderTransitionFrame, renderOverlayLayer } from '../compositor/canvasCompositor.js'
import { renderMixedAudioBuffer } from '../audio/audioMixer.js'
import { OfflineOverlayRenderer } from '../compositor/overlayRenderer.js'
import { computeVideoBitrate, AUDIO_BITRATE, configureHighQualityContext } from './qualitySettings.js'
import { normalizeToMp4 } from './normalizeToMp4.js'

const pickMimeType = () => {
    const candidates = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
    return candidates.find(type => window.MediaRecorder && MediaRecorder.isTypeSupported(type)) || 'video/webm'
}

// Real-time fallback export for browsers without full WebCodecs encode support (e.g. Safari):
// plays the composited timeline live at 1x speed while capturing canvas.captureStream() + the
// mixed audio (rendered offline, then played back live into a MediaStreamDestination) via
// MediaRecorder. Lower fidelity/slower than the WebCodecs pipeline, but works broadly.
// The capture itself may land in whatever container/codec the browser's MediaRecorder is willing
// to produce (webm on Chrome/Firefox/Edge) - normalizeToMp4 re-muxes/transcodes it into a real
// .mp4 before returning, so callers always get the same mp4 guarantee as the main pipeline.
export const exportTimelineViaMediaRecorder = async (timeline, { resolveSource, onProgress } = {}) => {
    // avc1 (the first candidate in pickMimeType) requires even dimensions too - see
    // exportTimeline.js's own comment on this same safety net.
    const { fps } = timeline.canvas
    const width = Math.round(timeline.canvas.width / 2) * 2
    const height = Math.round(timeline.canvas.height / 2) * 2
    const durationMs = computeTimelineDurationMs(timeline)
    if (durationMs <= 0) throw new Error('La timeline est vide.')

    const canvasSize = { width, height }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    configureHighQualityContext(ctx)

    const background = { mode: timeline.canvas.backgroundMode, color: timeline.canvas.backgroundColor }

    const mediaTrack = getMediaTrack(timeline)
    // Keyed by clip.id (not fileId): a clip split into two halves shares one source file with
    // itself, and a transition between them needs to seek two *different* positions in that file
    // at once - one shared element per fileId can only ever show one of the two.
    const mediaPool = new Map()
    for (const clip of mediaTrack.clips) {
        if (mediaPool.has(clip.id)) continue
        const source = resolveSource(clip.fileId)
        let el
        if (source.kind === 'image') {
            el = document.createElement('img')
            el.crossOrigin = 'anonymous'
            el.src = source.url
            await new Promise((resolve, reject) => { el.onload = resolve; el.onerror = reject })
        } else {
            el = document.createElement('video')
            el.crossOrigin = 'anonymous'
            el.muted = true
            el.playsInline = true
            el.preload = 'auto'
            el.src = source.url
            await new Promise((resolve) => { el.onloadeddata = resolve })
        }
        mediaPool.set(clip.id, { kind: source.kind, el })
    }

    const overlayTrack = timeline.tracks.find(t => t.type === 'overlay')
    const overlayRenderer = new OfflineOverlayRenderer(canvasSize)
    if (overlayTrack) await overlayRenderer.prepare(overlayTrack.overlays)

    let mixedAudioBuffer = null
    try {
        mixedAudioBuffer = await renderMixedAudioBuffer(timeline, resolveSource)
    } catch (e) {
        mixedAudioBuffer = null
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    const audioCtx = mixedAudioBuffer ? new AudioContextClass() : null
    const audioDestination = audioCtx ? audioCtx.createMediaStreamDestination() : null

    const videoStream = canvas.captureStream(fps)
    const combinedStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...(audioDestination ? audioDestination.stream.getAudioTracks() : [])
    ])

    const mimeType = pickMimeType()
    const videoBitrate = computeVideoBitrate(width, height, fps, timeline.exportSettings && timeline.exportSettings.videoBitrate)
    const recorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: videoBitrate,
        audioBitsPerSecond: AUDIO_BITRATE
    })
    const chunks = []
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }

    const recordingDone = new Promise((resolve) => { recorder.onstop = resolve })

    recorder.start()
    if (audioCtx && mixedAudioBuffer) {
        const bufferSource = audioCtx.createBufferSource()
        bufferSource.buffer = mixedAudioBuffer
        bufferSource.connect(audioDestination)
        bufferSource.start()
    }

    const startedAt = performance.now()

    await new Promise((resolve) => {
        const drawFrame = async () => {
            const elapsedMs = performance.now() - startedAt

            if (elapsedMs >= durationMs) {
                resolve()
                return
            }

            const transition = getTransitionAt(mediaTrack.clips, elapsedMs)
            if (transition) {
                const fromEntry = mediaPool.get(transition.fromClip.id)
                const toEntry = mediaPool.get(transition.toClip.id)
                const fromSourceMs = mapEffectiveMsToSourceMs(transition.fromClip, elapsedMs - transition.fromClip.timelineStartMs)
                // See exportTimeline.js's identical comment - the transition window is now
                // centered on the cut, so toClip needs to actually advance past frame 0 once
                // elapsedMs crosses its own timelineStartMs, not stay frozen for the whole window.
                const toSourceMs = mapEffectiveMsToSourceMs(transition.toClip, Math.max(0, elapsedMs - transition.toClip.timelineStartMs))
                if (fromEntry.kind === 'video') fromEntry.el.currentTime = fromSourceMs / 1000
                if (toEntry.kind === 'video') toEntry.el.currentTime = toSourceMs / 1000
                renderTransitionFrame(ctx, {
                    fromMediaEl: fromEntry.el,
                    fromClip: transition.fromClip,
                    toMediaEl: toEntry.el,
                    toClip: transition.toClip,
                    canvasSize,
                    type: transition.type,
                    direction: transition.direction,
                    progress: transition.progress,
                    background
                })
            } else {
                const clip = getActiveClipAtMs(timeline, elapsedMs)
                const entry = mediaPool.get(clip.id)
                const effectiveMs = elapsedMs - clip.timelineStartMs
                const sourceMs = mapEffectiveMsToSourceMs(clip, effectiveMs)
                if (entry.kind === 'video') {
                    entry.el.playbackRate = clip.playbackRate || 1
                    if (entry.el.paused) entry.el.play().catch(() => {})
                    if (Math.abs(entry.el.currentTime * 1000 - sourceMs) > 150) entry.el.currentTime = sourceMs / 1000
                }
                renderClipFrame(ctx, entry.el, canvasSize, clip, { background })
            }

            if (overlayTrack) {
                const overlayCanvasEl = overlayRenderer.renderAt(overlayTrack.overlays, elapsedMs)
                renderOverlayLayer(ctx, overlayCanvasEl, canvasSize)
            }

            if (onProgress) onProgress(Math.min(1, elapsedMs / durationMs))
            requestAnimationFrame(drawFrame)
        }
        requestAnimationFrame(drawFrame)
    })

    recorder.stop()
    await recordingDone

    overlayRenderer.dispose()
    if (audioCtx) audioCtx.close()
    mediaPool.forEach((entry) => { if (entry.kind === 'video') entry.el.pause() })

    const capturedBlob = new Blob(chunks, { type: mimeType })
    const normalizedBlob = await normalizeToMp4(capturedBlob, { videoBitrate, audioBitrate: AUDIO_BITRATE })

    if (onProgress) onProgress(1)

    return normalizedBlob
}
